import {
  cashLimitExceededMessage,
  exceedsCashLimit,
  type Operation,
  operationLevel,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  type PaymentMethod,
} from "@pcj/shared";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { Banner, Button, Card, Field, inputClass } from "../../components/ui";
import { errorMessage, issueFor } from "../../lib/api";
import { useCompany } from "./CompanyLayout";

/** New operation. A cash payment at or above the art. 32 limit cannot be saved. */
export function OperationForm({ onDone }: { onDone: (created: Operation | null) => void }) {
  const { clients, thresholds, today, actions, can, companyId } = useCompany();
  const [form, setForm] = useState({
    clientId: "",
    date: today,
    amount: "",
    paymentMethod: "transfer" as PaymentMethod,
    description: "",
  });
  const amount = Number.parseFloat(form.amount);
  const level = Number.isFinite(amount) ? operationLevel(amount, thresholds) : null;
  const cashOver =
    Number.isFinite(amount) && exceedsCashLimit({ paymentMethod: form.paymentMethod, amount }, thresholds);
  const error = actions.createOperation.error;
  const paymentIssue = issueFor(error, "paymentMethod");

  function submit(event: FormEvent) {
    event.preventDefault();
    if (cashOver) return;
    actions.createOperation.mutate({ ...form, amount }, { onSuccess: (created) => onDone(created) });
  }

  return (
    <Card className="p-4">
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <Field label="Cliente" required error={issueFor(error, "clientId")}>
          {(id) => (
            <select
              id={id}
              required
              className={inputClass}
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            >
              <option value="">Selecciona…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Fecha" required error={issueFor(error, "date")}>
          {(id) => (
            <input
              id={id}
              type="date"
              required
              max={today}
              className={inputClass}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          )}
        </Field>
        <Field
          label="Monto sin IVA (MXN)"
          required
          error={issueFor(error, "amount")}
          hint={
            level === "notice"
              ? "Alcanza el umbral de Aviso (1,605 UMA)."
              : level === "identification"
                ? "Alcanza el umbral de identificación (805 UMA): el expediente debe estar completo."
                : undefined
          }
        >
          {(id) => (
            <input
              id={id}
              type="number"
              min="0.01"
              step="0.01"
              required
              className={inputClass}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          )}
        </Field>
        <Field label="Forma de pago" required error={cashOver ? undefined : paymentIssue}>
          {(id) => (
            <select
              id={id}
              className={inputClass}
              value={form.paymentMethod}
              onChange={(e) => setForm({ ...form, paymentMethod: e.target.value as PaymentMethod })}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Descripción (opcional)" className="sm:col-span-2">
          {(id) => (
            <input
              id={id}
              className={inputClass}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          )}
        </Field>
        {cashOver && (
          <div className="sm:col-span-2">
            <Banner tone="error" title="No se puede registrar: excede el límite de efectivo">
              <p>{cashLimitExceededMessage(amount, thresholds)}</p>
              {can("configure") ? (
                <Link to={`/companies/${companyId}/settings`} className="mt-1 inline-block font-medium underline">
                  Revisar el límite en Configuración
                </Link>
              ) : (
                <p className="mt-1">
                  El límite depende del valor de la UMA que configura el Responsable de Cumplimiento.
                </p>
              )}
            </Banner>
          </div>
        )}
        {error && !issueFor(error, "amount") && !issueFor(error, "date") && !paymentIssue && (
          <div className="sm:col-span-2">
            <Banner tone="error">{errorMessage(error)}</Banner>
          </div>
        )}
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" loading={actions.createOperation.isPending} disabled={cashOver}>
            Registrar
          </Button>
          <Button variant="ghost" onClick={() => onDone(null)}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
